import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FormProgress } from '@hcc/types';
import { api } from '../../lib/api';
import type { OnboardingItemStatus } from './types';

export interface AssignableForm {
  id: string;
  title: string;
  assigned: boolean;
  status: OnboardingItemStatus;
  published: boolean;
  /** Completion of an in-progress draft, when the person has started one. */
  progress: FormProgress | null;
  draft_updated_at: string | null;
}

/** Which forms a specific person is (or isn't) assigned to fill in. */
export function usePersonForms(personId: string) {
  return useQuery({
    queryKey: ['person-forms', personId],
    queryFn: async () =>
      (
        await api<{ forms: AssignableForm[] }>(
          `/api/admin/onboarding/people/${personId}/forms`,
        )
      ).forms,
  });
}

/** An inductee's in-progress (unsubmitted) form answers, decrypted for HR. */
export interface PersonFormDraft {
  values: Record<string, unknown>;
  consent: boolean;
  form_version_id: string;
  updated_at: string | null;
}

/** Fetch the saved draft answers for one person + form (lazy via `enabled`). */
export function usePersonFormDraft(personId: string, formId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['person-form-draft', personId, formId],
    enabled,
    queryFn: () =>
      api<PersonFormDraft>(`/api/admin/onboarding/people/${personId}/forms/${formId}/draft`),
  });
}

export function useSetPersonForm(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ formId, assigned }: { formId: string; assigned: boolean }) =>
      api(`/api/admin/onboarding/people/${personId}/forms/${formId}`, {
        method: assigned ? 'POST' : 'DELETE',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['person-forms', personId] });
      qc.invalidateQueries({ queryKey: ['person-onboarding', personId] });
    },
  });
}

export function useReopenPersonForm(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (formId: string) =>
      api(`/api/admin/onboarding/people/${personId}/forms/${formId}/reopen`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['person-forms', personId] });
      qc.invalidateQueries({ queryKey: ['person-onboarding', personId] });
    },
  });
}
